import React from "react";
import { GraduationCap, UserCheck, CalendarClock, AlertOctagon, ShieldAlert } from "lucide-react";

function Card({ icon: Icon, label, headline, sub, tone = "sky", testid }) {
  const toneMap = {
    sky:     { rail: "before:bg-sky-500",     iconBg: "bg-sky-50 border-sky-100", iconText: "text-sky-700" },
    emerald: { rail: "before:bg-emerald-500", iconBg: "bg-emerald-50 border-emerald-100", iconText: "text-emerald-700" },
    amber:   { rail: "before:bg-amber-500",   iconBg: "bg-amber-50 border-amber-100", iconText: "text-amber-700" },
    red:     { rail: "before:bg-red-500",     iconBg: "bg-red-50 border-red-100", iconText: "text-red-700" },
    violet:  { rail: "before:bg-violet-500",  iconBg: "bg-violet-50 border-violet-100", iconText: "text-violet-700" },
  };
  const t = toneMap[tone] || toneMap.sky;
  return (
    <div
      data-testid={testid}
      className={`relative pl-4 pr-4 py-3.5 bg-white border border-slate-200 rounded-lg shadow-sm before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-lg ${t.rail}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
          <div className="mt-1 font-heading text-2xl font-semibold text-slate-900 tabular-nums">{headline}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 min-h-[16px]">{sub}</div>
        </div>
        <div className={`shrink-0 w-8 h-8 rounded-lg ${t.iconBg} border flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${t.iconText}`} />
        </div>
      </div>
    </div>
  );
}

export default function SummaryCards({ summary }) {
  const s = summary || {};
  const pct = summary ? `${(s.compliance_pct ?? 0).toFixed(1)}%` : "—";
  const pctTone = s.compliance_pct >= 90 ? "emerald" : s.compliance_pct >= 70 ? "amber" : (s.total_requirements ? "red" : "sky");
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="training-summary-cards">
      <Card
        icon={GraduationCap} label="Training Compliance"
        headline={pct}
        sub={summary ? `${s.compliant_requirements} of ${s.total_requirements} requirements met` : "Calculating…"}
        tone={pctTone}
        testid="tc-card-compliance"
      />
      <Card
        icon={UserCheck} label="Staff Fully Compliant"
        headline={summary ? `${s.fully_compliant}` : "—"}
        sub={summary ? `of ${s.staff_with_requirements} staff with requirements` : ""}
        tone={summary && s.fully_compliant === s.staff_with_requirements && s.staff_with_requirements ? "emerald" : "sky"}
        testid="tc-card-fully"
      />
      <Card
        icon={CalendarClock} label="Expiring in 30 Days"
        headline={summary ? `${s.expiring_soon}` : "—"}
        sub="Courses approaching renewal"
        tone={s.expiring_soon > 0 ? "amber" : "emerald"}
        testid="tc-card-expiring"
      />
      <Card
        icon={AlertOctagon} label="Expired / Overdue"
        headline={summary ? `${s.expired_or_overdue}` : "—"}
        sub="Past their expiry date"
        tone={s.expired_or_overdue > 0 ? "red" : "emerald"}
        testid="tc-card-expired"
      />
      <Card
        icon={ShieldAlert} label="Mandatory Training Gaps"
        headline={summary ? `${s.mandatory_gaps}` : "—"}
        sub="Required courses never completed"
        tone={s.mandatory_gaps > 0 ? "red" : "emerald"}
        testid="tc-card-gaps"
      />
    </div>
  );
}
